/**
 * SWARM Incident Reporter - Form Validation Utility
 * Provides comprehensive validation with helpful suggestions
 */

// Validation rules and messages
export const validationRules = {
  // Name validation
  name: {
    required: true,
    minLength: 2,
    maxLength: 100,
    pattern: /^[a-zA-Z\s\-'.]+$/,
    messages: {
      required: 'Please enter your full name.',
      minLength: 'Name must be at least 2 characters long.',
      maxLength: 'Name cannot exceed 100 characters.',
      pattern: 'Name can only contain letters, spaces, hyphens, apostrophes, and periods.',
      suggestions: [
        'Use your full legal name',
        'Avoid nicknames or abbreviations',
        'Include middle name if applicable',
      ],
    },
  },

  // Email validation
  email: {
    required: true,
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    maxLength: 254,
    messages: {
      required: 'Please enter your email address.',
      pattern: 'Please enter a valid email address.',
      maxLength: 'Email address is too long.',
      suggestions: [
        'Use a valid email format (e.g., user@domain.com)',
        'Check for typos in your email address',
        'Ensure the domain is correct',
      ],
    },
  },

  // Phone number validation
  phone: {
    required: false,
    pattern: /^[+]?[0-9\s\-()]{8,20}$/,
    messages: {
      pattern: 'Please enter a valid phone number.',
      suggestions: [
        'Include country code if international (e.g., +61)',
        'Use format: 0412 345 678 or (04) 1234 5678',
        'Avoid special characters except spaces, hyphens, and parentheses',
      ],
    },
  },

  // Location validation
  location: {
    required: true,
    minLength: 5,
    maxLength: 200,
    messages: {
      required: 'Please provide the incident location.',
      minLength: 'Location description is too short.',
      maxLength: 'Location description is too long.',
      suggestions: [
        'Include street name and suburb',
        'Add nearby landmarks if helpful',
        'Be as specific as possible',
      ],
    },
  },

  // Description validation
  description: {
    required: true,
    minLength: 10,
    maxLength: 2000,
    messages: {
      required: 'Please provide a description of the incident.',
      minLength: 'Description must be at least 10 characters long.',
      maxLength: 'Description cannot exceed 2000 characters.',
      suggestions: [
        'Include what happened, when, and who was involved',
        'Describe any vehicles, weapons, or other relevant details',
        'Mention any witnesses or evidence',
      ],
    },
  },

  // Incident time validation
  incidentTime: {
    required: true,
    messages: {
      required: 'Please provide the estimated time of the incident.',
      suggestions: [
        'Be as accurate as possible',
        'Include date and time',
        'Note if time is approximate',
      ],
    },
  },

  // Police report validation
  policeReport: {
    required: true,
    messages: {
      required: 'Please indicate if police or emergency services were in attendance.',
      suggestions: [
        'Select "Yes" if police or emergency services attended',
        'Select "No" if they did not attend',
        'Select "Unsure" if you\'re not certain',
      ],
    },
  },

  // Police report time validation
  policeReportTime: {
    required: false,
    conditional: form => form.reportedToPolice === 'yes',
    messages: {
      required: 'Please provide the time when police or emergency services attended.',
      suggestions: [
        'Include the date and time when police or emergency services attended',
        'This helps with official record keeping',
      ],
    },
  },

  // Injury count validation
  injuryCount: {
    required: false,
    conditional: form => form.injuries === 'yes',
    pattern: /^[1-9]\d*$/,
    min: 1,
    max: 50,
    messages: {
      pattern: 'Please enter a valid number of injuries.',
      min: 'Injury count must be at least 1.',
      max: 'Injury count cannot exceed 50.',
      suggestions: [
        'Enter the total number of people injured',
        'Use whole numbers only',
        'Include all injuries, even minor ones',
      ],
    },
  },

  // Vehicles involved validation
  vehiclesInvolved: {
    required: false,
    pattern: /^[1-9]\d*$/,
    min: 1,
    max: 50,
    messages: {
      pattern: 'Please enter a valid number of vehicles.',
      min: 'Vehicle count must be at least 1.',
      max: 'Vehicle count cannot exceed 50.',
      suggestions: [
        'Enter the total number of vehicles involved',
        'Use whole numbers only',
        'Leave blank if unknown',
      ],
    },
  },

  // Witness validation
  witnessedIncident: {
    required: true,
    messages: {
      required: 'Please indicate if you witnessed the incident.',
      suggestions: [
        'Select "Yes" if you saw the incident happen',
        'Select "No" if you heard about it from others',
        'Select "Unsure" if you\'re not certain',
      ],
    },
  },
};

// Validation functions
export const validators = {
  // Required field validation
  required: (value, fieldName) => {
    if (typeof value !== 'string') value = value == null ? '' : String(value);
    if (!value || value.trim() === '') {
      return {
        isValid: false,
        message: validationRules[fieldName]?.messages?.required || 'This field is required.',
      };
    }
    return { isValid: true };
  },

  // Minimum length validation
  minLength: (value, minLength, fieldName) => {
    if (typeof value !== 'string') value = value == null ? '' : String(value);
    if (value && value.length < minLength) {
      return {
        isValid: false,
        message:
          validationRules[fieldName]?.messages?.minLength ||
          `Must be at least ${minLength} characters.`,
      };
    }
    return { isValid: true };
  },

  // Maximum length validation
  maxLength: (value, maxLength, fieldName) => {
    if (typeof value !== 'string') value = value == null ? '' : String(value);
    if (value && value.length > maxLength) {
      return {
        isValid: false,
        message:
          validationRules[fieldName]?.messages?.maxLength ||
          `Cannot exceed ${maxLength} characters.`,
      };
    }
    return { isValid: true };
  },

  // Pattern validation
  pattern: (value, pattern, fieldName) => {
    if (typeof value !== 'string') value = value == null ? '' : String(value);
    if (value && !pattern.test(value)) {
      return {
        isValid: false,
        message: validationRules[fieldName]?.messages?.pattern || 'Invalid format.',
      };
    }
    return { isValid: true };
  },

  // Numeric range validation
  range: (value, min, max, fieldName) => {
    const num = parseInt(value);
    if (isNaN(num)) {
      return {
        isValid: false,
        message: validationRules[fieldName]?.messages?.pattern || 'Please enter a valid number.',
      };
    }
    if (num < min || num > max) {
      return {
        isValid: false,
        message:
          validationRules[fieldName]?.messages?.min || `Value must be between ${min} and ${max}.`,
      };
    }
    return { isValid: true };
  },

  // Conditional validation
  conditional: (value, condition, fieldName) => {
    if (typeof value !== 'string') value = value == null ? '' : String(value);
    if (condition && (!value || value.trim() === '')) {
      return {
        isValid: false,
        message: validationRules[fieldName]?.messages?.required || 'This field is required.',
      };
    }
    return { isValid: true };
  },
};

// Main validation function
export const validateField = (fieldName, value, form = {}) => {
  if (typeof value !== 'string') value = value == null ? '' : String(value);
  const rules = validationRules[fieldName];
  if (!rules) return { isValid: true };

  // Check if field is conditionally required
  if (rules.conditional && !rules.conditional(form)) {
    return { isValid: true };
  }

  // Required validation
  if (rules.required) {
    const requiredResult = validators.required(value, fieldName);
    if (!requiredResult.isValid) {
      return {
        ...requiredResult,
        suggestions: rules.messages?.suggestions || [],
      };
    }
  }

  // Skip other validations if field is empty and not required
  if (!value || value.trim() === '') {
    return { isValid: true };
  }

  // Min length validation
  if (rules.minLength) {
    const minLengthResult = validators.minLength(value, rules.minLength, fieldName);
    if (!minLengthResult.isValid) {
      return {
        ...minLengthResult,
        suggestions: rules.messages?.suggestions || [],
      };
    }
  }

  // Max length validation
  if (rules.maxLength) {
    const maxLengthResult = validators.maxLength(value, rules.maxLength, fieldName);
    if (!maxLengthResult.isValid) {
      return {
        ...maxLengthResult,
        suggestions: rules.messages?.suggestions || [],
      };
    }
  }

  // Pattern validation
  if (rules.pattern) {
    const patternResult = validators.pattern(value, rules.pattern, fieldName);
    if (!patternResult.isValid) {
      return {
        ...patternResult,
        suggestions: rules.messages?.suggestions || [],
      };
    }
  }

  // Range validation
  if (rules.min !== undefined && rules.max !== undefined) {
    const rangeResult = validators.range(value, rules.min, rules.max, fieldName);
    if (!rangeResult.isValid) {
      return {
        ...rangeResult,
        suggestions: rules.messages?.suggestions || [],
      };
    }
  }

  // Field is valid
  return {
    isValid: true,
    suggestions: rules.messages?.suggestions || [],
  };
};

// Validate entire form
export const validateForm = (form, isAdminMode = false) => {
  const errors = {};
  const warnings = {};
  const suggestions = {};

  // Define fields to validate based on mode
  const fieldsToValidate = isAdminMode
    ? ['location', 'description']
    : [
        'reporterName',
        'reporterEmail',
        'location',
        'description',
        'witnessedIncident',
        'estimatedIncidentTime',
        'reportedToPolice',
      ];

  // Validate each field
  fieldsToValidate.forEach(fieldName => {
    const result = validateField(fieldName, form[fieldName], form);

    if (!result.isValid) {
      errors[fieldName] = result.message;
      if (result.suggestions) {
        suggestions[fieldName] = result.suggestions;
      }
    } else if (result.suggestions) {
      suggestions[fieldName] = result.suggestions;
    }
  });

  // Conditional validations
  if (form.reportedToPolice === 'yes') {
    const policeTimeResult = validateField('policeReportTime', form.policeReportTime, form);
    if (!policeTimeResult.isValid) {
      errors.policeReportTime = policeTimeResult.message;
      if (policeTimeResult.suggestions) {
        suggestions.policeReportTime = policeTimeResult.suggestions;
      }
    }
  }

  if (form.injuries === 'yes') {
    const injuryCountResult = validateField('injuryCount', form.injuryCount, form);
    if (!injuryCountResult.isValid) {
      errors.injuryCount = injuryCountResult.message;
      if (injuryCountResult.suggestions) {
        suggestions.injuryCount = injuryCountResult.suggestions;
      }
    }
  }

  // Optional field validations (warnings)
  if (form.vehiclesInvolved) {
    const vehiclesResult = validateField('vehiclesInvolved', form.vehiclesInvolved, form);
    if (!vehiclesResult.isValid) {
      warnings.vehiclesInvolved = vehiclesResult.message;
      if (vehiclesResult.suggestions) {
        suggestions.vehiclesInvolved = vehiclesResult.suggestions;
      }
    }
  }

  if (form.reporterNumber) {
    const phoneResult = validateField('phone', form.reporterNumber, form);
    if (!phoneResult.isValid) {
      warnings.reporterNumber = phoneResult.message;
      if (phoneResult.suggestions) {
        suggestions.reporterNumber = phoneResult.suggestions;
      }
    }
  }

  return { errors, warnings, suggestions };
};

// Get field validation state for CSS classes
export const getFieldValidationState = (fieldName, value, form = {}) => {
  if (typeof value !== 'string') value = value == null ? '' : String(value);
  const result = validateField(fieldName, value, form);

  if (!result.isValid) {
    return 'error';
  }

  // Check if field has suggestions (could be considered a warning state)
  if (result.suggestions && result.suggestions.length > 0) {
    return 'warning';
  }

  // Check if field has value and is valid
  if (value && value.trim() !== '') {
    return 'success';
  }

  return 'default';
};

// Get character count for text fields
export const getCharacterCount = (value, maxLength) => {
  if (!value) return { count: 0, percentage: 0, status: 'default' };

  const count = value.length;
  const percentage = (count / maxLength) * 100;

  let status = 'default';
  if (percentage >= 90) status = 'at-limit';
  else if (percentage >= 75) status = 'near-limit';

  return { count, percentage, status };
};

// Format validation message with suggestions
export const formatValidationMessage = (message, suggestions = []) => {
  if (!suggestions || suggestions.length === 0) {
    return message;
  }

  const suggestionsList = suggestions.map(suggestion => `• ${suggestion}`).join('\n');
  return `${message}\n\nHelpful suggestions:\n${suggestionsList}`;
};

// Accessibility helpers
export const getFieldAriaAttributes = (fieldName, validationState, hasError, hasWarning) => {
  const attributes = {
    'aria-describedby': `${fieldName}-help`,
    'aria-invalid': hasError ? 'true' : 'false',
  };

  if (hasError) {
    attributes['aria-describedby'] += ` ${fieldName}-error`;
  }

  if (hasWarning) {
    attributes['aria-describedby'] += ` ${fieldName}-warning`;
  }

  return attributes;
};
